/* app/artifacts.js — engine-aware build-artifact registry.
   The Pylon layer runs many engines and each writes a DIFFERENT artifact tree.
   Rather than hardcode one tree in the UI, register a tree per engine here; the
   job-detail Artifacts panel renders whatever `forJob(job)` returns. Adding a new
   build engine later = one entry in TREES (+ a label) — no UI plumbing.

   Trees are grounded in the real run dirs (email-platform/swarm/runs/codex-*):
     - codex  → source render (_figma or _pdf), 03_execution, root _codex trace,
                and root manifest/events contract. NO 01_ingestion/02_planning,
                per-stage raw-response.json, or directory-only rows.
     - router/bullseye/legacy → the classic multi-stage swarm pipeline. */

(function () {
  // ---- CODEX (agentic) ----------------------------------------------------
  function codexTree(source) {
    const figma = source === 'figma';
    const sourceDir = figma ? '_figma/' : '_pdf/';
    const sourceLabel = figma ? 'Figma source render' : 'PDF source render';
    return [
      { kind: 'inputs', ico: 'image', n: sourceLabel, d: 'Design image given to Codex', dir: sourceDir, files: [
        { n: 'screenshot.png', d: 'Full source design', primary: true },
      ] },
      { ico: 'code', n: 'index.html', d: 'Execution · Codex CLI', dir: '03_execution/', phase: 0, files: [
        { n: 'index.html', d: 'The built email', primary: true },
        { n: 'vendor.stdout.log', d: 'codex exec stdout' },
      ] },
      { ico: 'json', n: 'Codex trace', d: 'Agent event stream', dir: '_codex/', phase: 1, files: [
        { n: 'stream-1.jsonl', d: 'Build pass — agent event stream', primary: true },
      ] },
      { ico: 'json', n: 'Run contract', d: 'Supervisor manifest + event log', dir: '', phase: 2, files: [
        { n: 'manifest.json', d: 'Stages-as-object · engine-agnostic contract', primary: true },
        { n: 'events.ndjson', d: 'Build event log' },
      ] },
    ];
  }

  // ---- SWARM (router · bullseye · legacy) — classic multi-stage -----------
  const SWARM = [
    { ico: 'doc', n: 'brief.md', d: 'Ingestion · 38K tok', dir: '01_ingestion/', phase: 0, files: [
      { n: 'brief.md', d: 'Structured creative brief', primary: true },
      { n: 'inventory.json', d: 'Source asset inventory' },
      { n: 'raw-response.json', d: 'Raw model output', raw: true },
    ] },
    { ico: 'json', n: 'semantic-frames.json', d: '6 element crops', dir: '01_6_semantic/', phase: 1, files: [
      { n: 'semantic-frames.json', d: 'Frame map', primary: true },
      { n: 'element-crops.json', d: 'Crop coordinates' },
      { n: 'vision-crop-plan.json', d: 'Vision crop plan' },
      { n: 'raw-response.json', d: 'Raw model output', raw: true },
    ] },
    { ico: 'doc', n: 'code-plan.md', d: 'Planning · build spec', dir: '02_planning/', phase: 2, files: [
      { n: 'code-plan.md', d: 'Build spec', primary: true },
      { n: 'detected-client.txt', d: 'Client detection' },
      { n: 'raw-response.json', d: 'Raw model output', raw: true },
    ] },
    { ico: 'code', n: 'index.html + images/', d: 'Execution output', dir: '03_execution/', phase: 3, files: [
      { n: 'index.html', d: 'Rendered email', primary: true },
      { n: 'images/', d: 'Copied build assets' },
      { n: 'raw-response.json', d: 'Raw model output', raw: true },
    ] },
    { ico: 'review', n: 'qa-report.md', d: 'Verdict + findings', dir: '04_qa/', phase: 4, files: [
      { n: 'qa-report.md', d: 'Verdict + findings', primary: true },
      { n: 'checklist-report.md', d: 'Checklist results' },
      { n: 'image-ref-audit.json', d: 'Broken-ref audit' },
      { n: 'vision-report.md', d: 'Visual-diff report' },
    ] },
    { ico: 'zip', n: 'delivery.zip', d: 'Delivery package', dir: '05_approval/', phase: 5, files: [
      { n: 'delivery.zip', d: 'Delivery package', primary: true },
      { n: 'approval.md', d: 'Approval verdict' },
      { n: 'changelog-entry.md', d: 'Changelog entry' },
    ] },
  ];

  // ---- SABRE (reasoning authority + deterministic executor) ---------------
  const SABRE = [
    { ico: 'json', n: 'Input manifest', d: 'Typed upload custody', dir: '01_ingestion/', phase: 0, files: [
      { n: 'input-manifest.v1.json', d: 'Immutable file inventory', primary: true },
      { n: 'file-role-receipt.json', d: 'Deterministic file roles' },
    ] },
    { ico: 'json', n: 'Design analysis', d: 'Obligations + approved reference', dir: '01_6_semantic/', phase: 1, files: [
      { n: 'design-obligation-inventory.v1.json', d: 'Source obligation inventory', primary: true },
      { n: 'reference-selection-receipt.v1.json', d: 'All scored reference candidates' },
    ] },
    { ico: 'doc', n: 'Sabre Decision Sheet', d: 'Structured implementation choices', dir: '02_planning/', phase: 2, files: [
      { n: 'sabre-decision-sheet.md', d: 'Developer-readable decision sheet', primary: true },
      { n: 'sabre-decision-proposal.v1.attempt-0.json', d: 'Validated structured proposal' },
      { n: 'sabre-decision-proposal.v1.attempt-1.json', d: 'Repair proposal when used' },
      { n: 'sabre-decision-proposal.v1.attempt-2.json', d: 'Final repair proposal when used' },
      { n: 'variant-profile-candidate.v1.json', d: 'Build-local profile candidate' },
      { n: 'sabre-decision-proposal.v2.attempt-0.json', d: 'Adaptive validated region graph' },
      { n: 'sabre-decision-proposal.v2.attempt-1.json', d: 'Adaptive repair graph when used' },
      { n: 'sabre-decision-proposal.v2.attempt-2.json', d: 'Final adaptive repair graph when used' },
      { n: 'variant-profile-candidate.v2.json', d: 'Adaptive build-local profile candidate' },
      { n: 'activated-profile-evidence.v1.json', d: 'Developer-reference extraction evidence' },
      { n: 'activated-profile-proposal.v1.json', d: 'Reviewed onboarding proposal' },
      { n: 'activated-variant-profile.v1.json', d: 'Exact selected active profile' },
      { n: 'activated-profile-approval.v1.json', d: 'Authenticated developer approval' },
      { n: 'activated-profile-activation.v1.json', d: 'Immutable activation receipt' },
    ] },
    { ico: 'code', n: 'Deterministic build', d: 'BuildContract-only execution', dir: '03_execution/', phase: 3, files: [
      { n: 'index.html', d: 'Candidate email', primary: true },
      { n: 'build-contract.v3.json', d: 'Sole builder authority' },
      { n: 'asset-manifest.v2.json', d: 'Bound assets and crops' },
      { n: 'build-receipt.json', d: 'Deterministic execution receipt' },
      { n: 'compile-receipt.json', d: 'Crop and compiler evidence receipt' },
      { n: 'crop-receipts/', d: 'Deterministic adaptive crop receipts' },
      { n: 'scope-resolution.json', d: 'Resolved authority scope' },
      { n: 'interaction-table.json', d: 'Bound labels and URLs' },
      { n: 'esp-capability-pack.json', d: 'ESP-owned region policy' },
      { n: 'images/', d: 'Bound build assets' },
    ] },
    { ico: 'review', n: 'Deterministic QA', d: 'Desktop/mobile + content gates', dir: '04_qa/', phase: 4, files: [
      { n: 'qa-report.md', d: 'Gate verdict and findings', primary: true },
      { n: 'qa-report.json', d: 'Machine-readable QA receipt' },
      { n: 'render-600.png', d: 'Desktop render' },
      { n: 'render-320.png', d: 'Mobile render' },
    ] },
    { ico: 'review', n: 'Developer review', d: 'No automatic promotion or delivery', dir: '05_approval/', phase: 5, files: [
      { n: 'approval.md', d: 'Review requirement', primary: true },
      { n: 'sabre-run-receipt.v1.json', d: 'Final immutable run receipt' },
      { n: 'needs-input.v1.json', d: 'Precise amended-upload request' },
    ] },
  ];

  const LABEL = { sabre: 'Sabre', codex: 'Codex CLI', router: 'Bullseye', bullseye: 'Bullseye', legacy: 'Legacy', claude: 'Claude' };

  function engineOf(job) { return (job && job.engine) || 'legacy'; }

  function forJob(job) {
    const engine = engineOf(job);
    const label = LABEL[engine] || 'Swarm';
    const isCodex = engine === 'codex';
    const tree = engine === 'codex' ? codexTree(job && job.source) : (engine === 'sabre' ? SABRE : SWARM);
    const st = job ? job.status : 'complete';

    if (isCodex) {
      // Completed Codex runs emit this canonical root-level set. During execution,
      // only the active execution group is unlocked; failed runs remain inspectable.
      const active = (st === 'running' || st === 'queued') ? 0 : 99;   // execution in-flight
      const done = (st === 'running' || st === 'queued') ? 0 : 99;     // everything else settled
      const groups = tree.map(g => {
        if (g.kind === 'inputs') return Object.assign({}, g, { state: 'done' });
        let state = 'locked';
        if (st === 'running' && g.phase === active) state = 'active';
        else if (g.phase < done) state = 'done';
        return Object.assign({}, g, { state });
      });
      return { engine, label, groups };
    }

    // swarm family — derive per-stage state from the job's 6-stage progress
    const stages = (job && job.stages) || [];
    const lastDone = stages.reduce((a, s, i) => s.status === 'done' ? i : a, -1);
    const failed = st === 'failed' ? stages.findIndex(s => s.status === 'failed') : -1;
    const groups = tree.map(g => {
      let state = (g.phase > lastDone && g.phase !== failed) ? 'locked' : 'done';
      if (g.phase === failed) state = 'failed';
      return Object.assign({}, g, { state });
    });
    return { engine, label, groups };
  }

  window.ARTIFACTS = { forJob, engineOf, LABEL };
})();
